import { useState, useRef, useEffect, useCallback } from 'react';
import './MentionTextarea.css';

/**
 * Column types allowed in the @ mention dropdown.
 * Add or remove types here to control what columns are surfaced.
 */
export const MENTION_COLUMN_TYPES = new Set([
  'text',
  'email',
  'long-text',
  'long_text',
  'numeric',
  'numbers',
  'status',
  'color',
  'link',
  'date',
  'phone',
  'location',
  'rating',
  'name',
]);

/**
 * MentionTextarea
 *
 * A controlled textarea that opens a column-picker dropdown when the user
 * types "/". Selecting a column inserts the token `{column_id}` at the
 * cursor. The backend resolves these tokens to real column values at send-time.
 *
 * Props:
 *   value        – controlled string value
 *   onChange     – (newValue: string) => void
 *   onBlur       – optional blur handler (used for autoSave)
 *   boardColumns – array of { id, title, type } from Monday
 *   placeholder  – textarea placeholder string
 *   rows         – number of visible rows (default 5)
 *   className    – extra className forwarded to the inner <textarea>
 *   style        – inline style forwarded to the inner <textarea> (the CSS file
 *                  only supplies resets, so the caller owns the appearance)
 *   singleLine   – treat the value as one line: Enter never inserts a newline
 *                  and pasted newlines collapse to spaces. For fields like a
 *                  file name where a line break is not a legal value.
 */
const MentionTextarea = ({
  value,
  onChange,
  onBlur,
  boardColumns = [],
  placeholder,
  rows = 5,
  className = '',
  style,
  singleLine = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [triggerIndex, setTriggerIndex] = useState(-1); // char index of the '/'
  const [query, setQuery] = useState('');               // chars typed after '/'
  const [activeIdx, setActiveIdx] = useState(0);        // keyboard-highlighted row

  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);

  // Columns eligible for mention, filtered by type then by the current query
  const filteredColumns = boardColumns
    .filter((col) => MENTION_COLUMN_TYPES.has(col.type))
    .filter((col) => col.title.toLowerCase().includes(query.toLowerCase()));

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setTriggerIndex(-1);
    setQuery('');
    setActiveIdx(0);
  }, []);

  /** Replace the "@query" span with the resolved token and close the menu */
  const insertToken = useCallback(
    (col) => {
      const token = `{${col.id}}`;
      // triggerIndex is where '/' sits; we also consume the query typed after it
      const before = value.slice(0, triggerIndex);
      const after = value.slice(triggerIndex + 1 + query.length);
      const newValue = before + token + after;
      onChange(newValue);
      closeMenu();

      // Restore focus and place cursor right after the inserted token
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        const pos = triggerIndex + token.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      });
    },
    [value, triggerIndex, query, onChange, closeMenu],
  );

  // Close on click outside the wrapper
  useEffect(() => {
    if (!menuOpen) return;
    const onMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuOpen, closeMenu]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    // Open menu when '/' is typed (and menu is not already open)
    if (e.key === '/' && !menuOpen) {
      setTriggerIndex(e.target.selectionStart); // position where '/' will land
      setQuery('');
      setActiveIdx(0);
      setMenuOpen(true);
      return; // let the character be typed; handleChange fires next
    }

    // With the menu closed, Enter would insert a newline. In single-line mode
    // there is no such thing as a second line, so swallow it. (With the menu
    // open Enter means "pick the highlighted column" — handled below.)
    if (singleLine && e.key === 'Enter' && !menuOpen) {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    if (!menuOpen) return;

    // Navigation inside the open menu
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, filteredColumns.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (filteredColumns.length > 0) {
          e.preventDefault();
          insertToken(filteredColumns[activeIdx]);
        }
        break;
      case 'Escape':
        closeMenu();
        break;
      case 'Backspace': {
        // Close if the user backspaces past (or onto) the '@' trigger
        const cursor = e.target.selectionStart;
        if (cursor <= triggerIndex + 1) closeMenu();
        break;
      }
      default:
        break;
    }
  };

  const handleChange = (e) => {
    // Paste is the other way a newline arrives; collapse rather than drop, so
    // pasting two lines does not silently weld the last and first words.
    const newVal = singleLine ? e.target.value.replace(/[\r\n]+/g, ' ') : e.target.value;
    onChange(newVal);

    if (!menuOpen) return;

    const cursor = e.target.selectionStart;

    // Close if cursor moved behind trigger
    if (cursor <= triggerIndex) {
      closeMenu();
      return;
    }

    const newQuery = newVal.slice(triggerIndex + 1, cursor);

    // Close if user typed another '/' or a newline inside the query
    if (newQuery.includes('/') || newQuery.includes('\n')) {
      closeMenu();
      return;
    }

    setQuery(newQuery);
    setActiveIdx(0);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div ref={wrapperRef} className='mention-textarea-wrapper'>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        className={`mention-textarea-inner ${className}`}
        style={style}
      />

      {menuOpen && (
        <div className='mention-dropdown' role='listbox' aria-label='Board columns'>
          {filteredColumns.length === 0 ? (
            <div className='mention-dropdown-empty'>No matching columns</div>
          ) : (
            filteredColumns.map((col, idx) => (
              <div
                key={col.id}
                role='option'
                aria-selected={idx === activeIdx}
                className={`mention-dropdown-item ${idx === activeIdx ? 'mention-dropdown-item--active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent textarea blur before insert
                  insertToken(col);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
              >
                <span className='mention-dropdown-item-title'>{col.title}</span>
                <span className='mention-dropdown-item-id'>{col.id}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
